import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Flame, Plus, Eye, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const tipoLabels = {
    colisao: 'Colisão',
    encalhe: 'Encalhe',
    incendio: 'Incêndio',
    vazamento: 'Vazamento',
    acidente_pessoal: 'Acidente Pessoal',
    avaria: 'Avaria',
    outros: 'Outros'
};

const statusLabels = {
    aberto: 'Aberto',
    em_tratativa: 'Em Tratativa',
    encerrado: 'Encerrado'
};

const SinistrosPage = () => {
    const [sinistros, setSinistros] = useState([]);
    const [roos, setRoos] = useState([]);
    const [convoys, setConvoys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedSinistro, setSelectedSinistro] = useState(null);
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        tipo: 'outros',
        roo_id: '',
        comboio_id: '',
        impacto_humano: '',
        impacto_ambiental: '',
        impacto_material: '',
        acoes_emergenciais: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [sinistrosRes, roosRes, convoysRes] = await Promise.all([
                axios.get(`${API}/sinistros`),
                axios.get(`${API}/roos`),
                axios.get(`${API}/convoys`)
            ]);
            setSinistros(sinistrosRes.data);
            setRoos(roosRes.data.filter(r => r.status !== 'encerrado'));
            setConvoys(convoysRes.data);
        } catch (error) {
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.roo_id) {
            toast.error('É obrigatório vincular um ROO');
            return;
        }

        try {
            await axios.post(`${API}/sinistros`, formData);
            toast.success('Sinistro registrado!');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar sinistro');
        }
    };

    const handleStatusChange = async (sinistroId, newStatus) => {
        try {
            await axios.put(`${API}/sinistros/${sinistroId}`, { status: newStatus });
            toast.success('Status atualizado!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
        }
    };

    const addTimelineEvent = async (sinistroId, evento) => {
        try {
            await axios.put(`${API}/sinistros/${sinistroId}`, { evento_timeline: evento });
            toast.success('Evento adicionado à linha do tempo');
            fetchData();
        } catch (error) {
            toast.error('Erro ao adicionar evento');
        }
    };

    const resetForm = () => {
        setFormData({
            titulo: '',
            descricao: '',
            tipo: 'outros',
            roo_id: '',
            comboio_id: '',
            impacto_humano: '',
            impacto_ambiental: '',
            impacto_material: '',
            acoes_emergenciais: ''
        });
    };

    const canCreate = hasRole(['admin', 'gestor', 'operador']);
    const canValidate = hasRole(['admin', 'gestor']);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="sinistros-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Sinistros Operacionais
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Registro e acompanhamento de sinistros
                    </p>
                </div>
                
                {canCreate && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-sinistro"
                                className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Registrar Sinistro
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Registrar Sinistro
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Título *
                                        </Label>
                                        <Input
                                            data-testid="input-sinistro-titulo"
                                            value={formData.titulo}
                                            onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                                            required
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Tipo *
                                        </Label>
                                        <Select 
                                            value={formData.tipo} 
                                            onValueChange={(value) => setFormData({...formData, tipo: value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(tipoLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            ROO Vinculado *
                                        </Label>
                                        <Select 
                                            value={formData.roo_id} 
                                            onValueChange={(value) => setFormData({...formData, roo_id: value})}
                                        >
                                            <SelectTrigger data-testid="select-sinistro-roo" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue placeholder="Selecione o ROO" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {roos.map((roo) => (
                                                    <SelectItem key={roo.id} value={roo.id}>
                                                        {roo.codigo} - {roo.titulo}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Comboio (opcional)
                                        </Label>
                                        <Select 
                                            value={formData.comboio_id || "none"} 
                                            onValueChange={(value) => setFormData({...formData, comboio_id: value === "none" ? "" : value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue placeholder="Selecione..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                <SelectItem value="none">Nenhum</SelectItem>
                                                {convoys.map((convoy) => (
                                                    <SelectItem key={convoy.id} value={convoy.id}>
                                                        {convoy.nome}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Descrição *
                                    </Label>
                                    <Textarea
                                        data-testid="input-sinistro-descricao"
                                        value={formData.descricao}
                                        onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                                        required
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[100px]"
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Impacto Humano
                                        </Label>
                                        <Textarea
                                            value={formData.impacto_humano}
                                            onChange={(e) => setFormData({...formData, impacto_humano: e.target.value})}
                                            placeholder="Feridos, vítimas..."
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Impacto Ambiental
                                        </Label>
                                        <Textarea
                                            value={formData.impacto_ambiental}
                                            onChange={(e) => setFormData({...formData, impacto_ambiental: e.target.value})}
                                            placeholder="Poluição, danos..."
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Impacto Material
                                        </Label>
                                        <Textarea
                                            value={formData.impacto_material}
                                            onChange={(e) => setFormData({...formData, impacto_material: e.target.value})}
                                            placeholder="Danos estruturais..."
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Ações Emergenciais
                                    </Label>
                                    <Textarea
                                        value={formData.acoes_emergenciais}
                                        onChange={(e) => setFormData({...formData, acoes_emergenciais: e.target.value})}
                                        placeholder="Ações tomadas imediatamente..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-sinistro"
                                        className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-sm"
                                    >
                                        Registrar Sinistro
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {sinistros.length === 0 ? (
                        <div className="p-12 text-center">
                            <Flame className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhum sinistro registrado</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Código</TableHead>
                                    <TableHead>Título</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>ROO</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sinistros.map((sinistro) => (
                                    <TableRow key={sinistro.id} data-testid={`sinistro-row-${sinistro.id}`}>
                                        <TableCell className="font-mono text-red-400">
                                            {sinistro.codigo}
                                        </TableCell>
                                        <TableCell className="text-white">
                                            {sinistro.titulo}
                                        </TableCell>
                                        <TableCell className="text-slate-300">
                                            {tipoLabels[sinistro.tipo]}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${sinistro.status === 'aberto' ? 'badge-danger' : ''}
                                                ${sinistro.status === 'em_tratativa' ? 'badge-warning' : ''}
                                                ${sinistro.status === 'encerrado' ? 'badge-success' : ''}
                                            `}>
                                                {statusLabels[sinistro.status]}
                                            </span>
                                        </TableCell>
                                        <TableCell className="font-mono text-cyan-400 text-xs">
                                            {sinistro.roo_id?.slice(0, 8)}...
                                        </TableCell>
                                        <TableCell className="text-slate-500 text-xs font-mono">
                                            {formatDate(sinistro.criado_em)}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedSinistro(sinistro);
                                                    setDetailDialogOpen(true);
                                                }}
                                                className="text-slate-400 hover:text-white hover:bg-slate-800"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Flame className="w-5 h-5 text-red-400" />
                            {selectedSinistro?.codigo}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedSinistro && (
                        <div className="space-y-6 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Título</p>
                                    <p className="text-white mt-1">{selectedSinistro.titulo}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Tipo</p>
                                    <p className="text-white mt-1">{tipoLabels[selectedSinistro.tipo]}</p>
                                </div>
                            </div>

                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase">Descrição</p>
                                <p className="text-slate-300 mt-1 text-sm">{selectedSinistro.descricao}</p>
                            </div>

                            {(selectedSinistro.impacto_humano || selectedSinistro.impacto_ambiental || selectedSinistro.impacto_material) && (
                                <div className="grid grid-cols-3 gap-4">
                                    {selectedSinistro.impacto_humano && (
                                        <div>
                                            <p className="text-xs text-slate-500 font-mono uppercase">Impacto Humano</p>
                                            <p className="text-red-400 mt-1 text-sm">{selectedSinistro.impacto_humano}</p>
                                        </div>
                                    )}
                                    {selectedSinistro.impacto_ambiental && (
                                        <div>
                                            <p className="text-xs text-slate-500 font-mono uppercase">Impacto Ambiental</p>
                                            <p className="text-amber-400 mt-1 text-sm">{selectedSinistro.impacto_ambiental}</p>
                                        </div>
                                    )}
                                    {selectedSinistro.impacto_material && (
                                        <div>
                                            <p className="text-xs text-slate-500 font-mono uppercase">Impacto Material</p>
                                            <p className="text-slate-300 mt-1 text-sm">{selectedSinistro.impacto_material}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Timeline */}
                            {selectedSinistro.linha_do_tempo?.length > 0 && (
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase mb-3 flex items-center gap-2">
                                        <Clock className="w-3 h-3" />
                                        Linha do Tempo
                                    </p>
                                    <div className="space-y-2 border-l-2 border-slate-700 pl-4">
                                        {selectedSinistro.linha_do_tempo.map((evento, idx) => (
                                            <div key={idx} className="relative">
                                                <div className="absolute -left-[21px] top-1 w-2 h-2 rounded-full bg-cyan-500" />
                                                <p className="text-sm text-white">{evento.evento}</p>
                                                <p className="text-xs text-slate-500">
                                                    {evento.usuario} - {formatDate(evento.timestamp)}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            {selectedSinistro.status !== 'encerrado' && (
                                <div className="border-t border-slate-800 pt-4 flex gap-2">
                                    {selectedSinistro.status === 'aberto' && (
                                        <Button
                                            onClick={() => handleStatusChange(selectedSinistro.id, 'em_tratativa')}
                                            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs"
                                        >
                                            Iniciar Tratativa
                                        </Button>
                                    )}
                                    {canValidate && (
                                        <Button
                                            onClick={() => handleStatusChange(selectedSinistro.id, 'encerrado')}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs"
                                        >
                                            Encerrar Sinistro
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SinistrosPage;
